import { useEffect, useState } from "react";
import { SubmitHandler, UseFormReturn } from "react-hook-form";
import { Groups } from "../groups/Groups";
import { useCreateRecord } from "../../api/useCreateRecord";
import { useUpdateRecord } from "../../api/useUpdateRecord";
import { sanitizeData } from "../../utils/sanitizeFormData";
import { setFieldsErrors } from "../../utils/setFieldsErrors";
import { GroupsOptions } from "../../types/groups.types";
import { useStore } from "../../store/store";
import { pick } from "lodash";
import { FormFields } from "./EntityForm";
import { FormatedFields } from "../../types/fields.types";
import { Box, Button } from "@mui/material";
import { Edit, Save, Close } from "@mui/icons-material";

type EntityFormFieldSpecificProps = {
	item: any;
	formatedFields: FormatedFields;
	entityName: string;
	entityForm: UseFormReturn<FormFields>;
	groupsOptions?: GroupsOptions;
};

export const EntityFormFieldSpecific = ({
	item,
	formatedFields,
	entityName,
	entityForm,
	groupsOptions,
}: EntityFormFieldSpecificProps) => {
	const createRecord = useCreateRecord({ queryInvalidatePrefix: entityName });
	const updateRecord = useUpdateRecord({ queryInvalidatePrefix: entityName });
	const { notifications } = useStore();
	const [type, setType] = useState<"detail" | "update" | "create">("detail");

	if (entityName === "clients")
		console.error("This component cannot be used for clients entity, see useUpdateRecord");

	useEffect(() => {
		if (!entityForm) return;
		if (updateRecord.isError && Object.keys(entityForm.formState.errors).length === 0) {
			const errors = updateRecord.error.response.data.errors;
			const otherErrors = errors[0].extensions.code;
			if (
				(otherErrors === "INTERNAL_SERVER_ERROR" || otherErrors === "FORBIDDEN") &&
				!notifications.notification.isOpen
			) {
				notifications.setNotification({
					isOpen: true,
					message: `Error - ${errors[0].message}`,
					severity: "error",
				});
			}

			if (otherErrors !== "INTERNAL_SERVER_ERROR" && otherErrors !== "FORBIDDEN") {
				setFieldsErrors(errors, entityForm);
			}
		}

		if (createRecord.isSuccess && createRecord.variables) {
			entityForm.reset();
		} else if (createRecord.isError && Object.keys(entityForm.formState.errors).length === 0) {
			const errors = createRecord.error.response.data.errors;
			const otherErrors = errors[0].extensions.code;

			if (
				(otherErrors === "INTERNAL_SERVER_ERROR" || otherErrors === "FORBIDDEN") &&
				!notifications.notification.isOpen
			) {
				notifications.setNotification({
					isOpen: true,
					message: `Error - ${errors[0].message}`,
					severity: "error",
				});
			}
			if (otherErrors !== "INTERNAL_SERVER_ERROR" && otherErrors !== "FORBIDDEN") {
				setFieldsErrors(errors, entityForm);
			}
		}
	}, [createRecord, updateRecord, entityForm, notifications]);

	const onSubmit: SubmitHandler<FormFields> = async (data) => {
		if (!entityForm) return;

		const sanitizedData = sanitizeData(data, formatedFields);

		if (groupsOptions && groupsOptions.isFormValid) {
			const validation = groupsOptions.isFormValid(sanitizedData);

			if (validation.valid === false) {
				notifications.setNotification({
					isOpen: true,
					message: validation.message,
					severity: "error",
				});
				return;
			}
		}

		if (type === "update") {
			if (!item?.id) return;
			const dirtyData = pick(data, Object.keys(entityForm.formState.dirtyFields));

			try {
				const updatedData = await updateRecord.mutateAsync({
					data: dirtyData,
					entity: entityName,
					id: item.id,
				});

				if (groupsOptions && groupsOptions.doStuffAfterUpdate && Object.keys(dirtyData).length > 0) {
					const response = await groupsOptions.doStuffAfterUpdate(item, updatedData, Object.keys(dirtyData));

					notifications.setNotification({
						isOpen: true,
						message: response?.message,
						severity: response?.severity,
					});
				} else if (updateRecord.isSuccess) {
					notifications.setNotification({
						isOpen: true,
						message: "Dados atualizados com sucesso.",
						severity: "success",
					});
				}

				setType("detail");
			} catch (error: any) {
				notifications.setNotification({
					isOpen: true,
					message: error.response.data.errors[0].message,
					severity: "error",
				});
			}
		}

		if (type === "create") {
			try {
				await createRecord.mutateAsync({ data: sanitizedData, entity: entityName });
				if (createRecord.isSuccess) {
					notifications.setNotification({
						isOpen: true,
						message: "Dados criados com sucesso.",
						severity: "success",
					});
				}
				setType("detail");
			} catch (error: any) {
				notifications.setNotification({
					isOpen: true,
					message: error.response.data.errors[0].message,
					severity: "error",
				});
			}
		}
	};

	return (
		<form
			id={entityName}
			onSubmit={(e) => {
				if (!entityForm) return;
				entityForm.clearErrors();
				entityForm.handleSubmit(onSubmit)(e);
			}}
			style={{ width: "100%", backgroundColor: "#fff" }}
		>
			<Groups
				item={item}
				fields={formatedFields}
				entityForm={entityForm}
				type={type}
				groupsOptions={groupsOptions}
			/>
			<Box px={4} display="flex" justifyContent={type === "detail" ? "flex-start" : "flex-end"} gap={2}>
				{type === "detail" ? (
					<Button
						fullWidth
						sx={{ maxWidth: "400px" }}
						type="button"
						onClick={(e) => {
							e.preventDefault();
							setType("update");
						}}
						startIcon={<Edit />}
						disabled={updateRecord.isLoading}
					>
						Editar
					</Button>
				) : (
					<>
						<Button
							color="secondary"
							type="button"
							startIcon={<Close />}
							onClick={(e) => {
								e.preventDefault();
								setType("detail");
							}}
							disabled={updateRecord.isLoading}
						>
							Cancelar
						</Button>
						<Button type="submit" startIcon={<Save />} disabled={updateRecord.isLoading}>
							Gravar
						</Button>
					</>
				)}
			</Box>
		</form>
	);
};
